import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {BehaviorSubject, filter, Observable, pipe, switchMap, take, tap, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AuthService} from "../../auth/auth.service";
import {environment} from "../../../../../environments/environment";
import { BsaAuthTokenXBVO } from 'src/app/skitbs/model/dto.models.imported';

/**
 * Provide custom json parsing capabilities for api requests.
 * @export
 * @class JsonInterceptor
 */
@Injectable()
export class RestAuthInterceptor implements HttpInterceptor {

    private TAG = "[RestAuthInterceptor] ";

    private isRefreshing = false;

    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    /**
     * Constructor
     * @param authService
     */
    constructor(private authService: AuthService) {
    }

    /**
     * Intercepts REST-Calls
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // skip
        if (req.url.includes("/auth")) {
            return next.handle(req);
        }

        // logout when no Token
        let pToken = this.authService.getToken();
        if (pToken == null) {
            this.authService.logout();
            return null;
        }

        // create HttpRequest with Auth-header
        let reqWithAuth = this.addTokenHeader(req, pToken);

        // call
        return next
            .handle(reqWithAuth)
            .pipe(
                // try refresh when 401
                catchError(error => {
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        return this.refreshToken_On_401_Unauthorized(reqWithAuth, next);
                    }
                    return throwError(error);
                }));

    }

    private refreshToken_On_401_Unauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // I phase: START Token-refresh...
        if (!this.isRefreshing) {
            this.logd("Token-refresh: START");
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);
            const pRefreshToken = this.authService.getRefreshToken();
            if (pRefreshToken)
                return this.authService.refreshToken(pRefreshToken).pipe(
                    switchMap((aTokenSession: BsaAuthTokenXBVO) => {
                        this.isRefreshing = false;
                        let pNewToken = aTokenSession.token;
                        this.logd("Token-refresh: GOT NEW TOKEN");
                        this.authService.refreshCurrentSession(aTokenSession);
                        this.refreshTokenSubject.next(pNewToken);
                        return next.handle(this.addTokenHeader(request, pNewToken));
                    }),
                    catchError((err) => {
                        // network problems | refresh-token ist too old
                        this.logd("ERROR: " + err.get);
                        this.isRefreshing = false;
                        this.logd("Token-refresh: ERROR --> LOGOUT");
                        this.authService.logout();
                        return throwError(err);
                    })
                );
        }

        // II phase: COMPLETED Token-refresh...
        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            pipe(tap((a) => this.logd("Token-refresh: FINISHED"))),
            switchMap((token) => next.handle(this.addTokenHeader(request, token)))
        );
    }

    /**
     * Creates Bearer-Token.
     * @private
     */
    private addTokenHeader(req: HttpRequest<any>, aToken: string): HttpRequest<any> {
      if (!environment.production) {
        this.logd("[JWT] Attach Bearer-Token:" + AuthService.parseTokenInfo(aToken));
      }
      return req.clone({
        headers: new HttpHeaders().append('Authorization', `Bearer ${aToken}`)
      });
    }

    /**
     * Logs.
     * @param s
     * @private
     */
    private logd(s: string) {
        if (!environment.production) {
            console.log(this.TAG + s);
        }
    }


}
